/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef, Type } from "@angular/core";

import { NzButtonType } from "ng-zorro-antd/button";
import { NzSafeAny } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";

import { ModalButtonOptions, OnClickCallback, StyleObjectLike } from "./modal.models";

import { ModalTypes, NzModalComponent } from "ng-zorro-antd/modal";

@Component({
  selector: "ui-modal",
  exportAs: "uiModal",
  templateUrl: "./modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["modal.component.less"],
})
export class UiModalComponent<T = NzSafeAny, R = NzSafeAny> extends NzModalComponent implements OnInit {
  @Input("modalMask") @InputBoolean() nzMask?: boolean;
  @Input("modalMaskClosable") @InputBoolean() nzMaskClosable?: boolean;
  @Input("modalCloseOnNavigation") @InputBoolean() nzCloseOnNavigation?: boolean;
  @Input("modalVisible") @InputBoolean() nzVisible = false;
  @Input("modalClosable") @InputBoolean() nzClosable = true;
  @Input("modalOkLoading") @InputBoolean() nzOkLoading = false;
  @Input("modalOkDisabled") @InputBoolean() nzOkDisabled = false;
  @Input("modalCancelDisabled") @InputBoolean() nzCancelDisabled = false;
  @Input("modalCancelLoading") @InputBoolean() nzCancelLoading = false;
  @Input("modalKeyboard") @InputBoolean() nzKeyboard = true;
  @Input("modalNoAnimation") @InputBoolean() nzNoAnimation = false;
  @Input("modalCentered") @InputBoolean() nzCentered = false;
  @Input("modalContent") nzContent?: string | TemplateRef<object> | Type<T>;
  @Input("modalComponentParams") nzComponentParams?: T;
  @Input("footer") nzFooter?: string | TemplateRef<object> | Array<ModalButtonOptions<T>> | null;
  @Input("modalZIndex") nzZIndex = 1000;
  @Input("modalWidth") nzWidth: number | string = 520;
  @Input("modalWrapClassName") nzWrapClassName?: string;
  @Input("modalClassName") nzClassName?: string;
  @Input("modalStyle") nzStyle?: object;
  @Input("title") nzTitle?: string | TemplateRef<object>;
  @Input("modalCloseIcon") nzCloseIcon: string | TemplateRef<void> = "close";
  @Input("modalMaskStyle") nzMaskStyle?: StyleObjectLike;
  @Input("modalBodyStyle") nzBodyStyle?: StyleObjectLike;
  @Input("modalOkText") nzOkText?: string | null;
  @Input("modalCancelText") nzCancelText?: string | null;
  @Input("modalOkType") nzOkType: NzButtonType = "primary";
  @Input("modalOkDanger") @InputBoolean() nzOkDanger = false;
  @Input("modalIconType") nzIconType = "question-circle"; // Confirm Modal ONLY
  @Input("modalType") nzModalType: ModalTypes = "default";
  @Input("modalAutofocus") nzAutofocus: "ok" | "cancel" | "auto" | null = "auto";

  // TODO(@from ng-zorro) Input will not be supported
  @Input()
  @Output()
  modalOnOk: EventEmitter<T> | OnClickCallback<T> | NzSafeAny = new EventEmitter<T>();

  // TODO(@from ng-zorro) Input will not be supported
  @Input()
  @Output()
  modalOnCancel: EventEmitter<T> | OnClickCallback<T> | NzSafeAny = new EventEmitter<T>();

  @Output() modalAfterOpen = new EventEmitter<void>();
  @Output() modalAfterClose = new EventEmitter<R>();
  @Output() modalVisibleChange = new EventEmitter<boolean>();

  ngOnInit(): void {
    // map events
    this.modalOnOk = this.nzOnOk;
    this.modalOnCancel = this.nzOnCancel;
    this.modalAfterOpen = this.nzAfterOpen;
    this.modalAfterClose = this.nzAfterClose;
    this.modalVisibleChange = this.nzVisibleChange;
  }
}
